import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import nciAggregates from './data/nci_aggregates.json';
import teisAggregates from './data/teis_aggregates.json';
import cecAggregates from './data/cec_aggregates.json';
import type { Project, DatasetType } from '../Layout/MainLayout';

// ---------------------------------------------------------------------------
// NCI data (per-scenario aggregation produced by aggregate_nci.py + slide-
// validated headline table).
// ---------------------------------------------------------------------------

interface AggScenario {
  key: string;
  year: number;
  level: string;
  fleet: number;
  daily_energy_gwh: number;
  chargers: {
    total: number;
    depot: number;
    public: number;
    by_power_kw: Array<{ power_kw: number; count: number }>;
  };
  vehicle_to_charger_ratio: number;
  hourly_load_mw: number[];
}
const AGG: AggScenario[] = (nciAggregates as { scenarios: AggScenario[] }).scenarios;

const POWER_LEVELS: number[] = Array.from(
  new Set(AGG.flatMap((s) => s.chargers.by_power_kw.map((p) => p.power_kw))),
).sort((a, b) => a - b);

const POWER_MIX_DATA = AGG.map((s) => {
  const row: Record<string, number | string> = { scenario: scenarioLabel(s) };
  for (const lvl of POWER_LEVELS) {
    const hit = s.chargers.by_power_kw.find((p) => p.power_kw === lvl);
    row[`${lvl} kW`] = hit ? hit.count : 0;
  }
  return row;
});

const HOURLY_LOAD_DATA = Array.from({ length: 24 }, (_, h) => {
  const row: Record<string, number | string> = { hour: h };
  for (const s of AGG) row[scenarioLabel(s)] = s.hourly_load_mw[h];
  return row;
});

function scenarioLabel(s: AggScenario | { year: number; level: string }): string {
  return `${s.year} ${s.level}`;
}

// Slide-validated headline table including 2040 rows for context (greyed).
interface ScenarioRow {
  scenario: string;
  year: 2030 | 2035 | 2040;
  level: 'Low' | 'Mid' | 'High';
  fleet: number;
  energy: number;
  depotShare: number;
  available: boolean;
}
const NCI_DATA: ScenarioRow[] = [
  { scenario: '2030 Low',  year: 2030, level: 'Low',  fleet: 12000,   energy: 2.2,   depotShare: 76, available: true },
  { scenario: '2030 Mid',  year: 2030, level: 'Mid',  fleet: 24000,   energy: 4.8,   depotShare: 79, available: true },
  { scenario: '2030 High', year: 2030, level: 'High', fleet: 76000,   energy: 12.1,  depotShare: 77, available: true },
  { scenario: '2035 Low',  year: 2035, level: 'Low',  fleet: 65000,   energy: 8.5,   depotShare: 79, available: true },
  { scenario: '2035 Mid',  year: 2035, level: 'Mid',  fleet: 134000,  energy: 20.9,  depotShare: 73, available: true },
  { scenario: '2035 High', year: 2035, level: 'High', fleet: 704000,  energy: 84.4,  depotShare: 78, available: true },
  { scenario: '2040 Low',  year: 2040, level: 'Low',  fleet: 198000,  energy: 22.0,  depotShare: 80, available: false },
  { scenario: '2040 Mid',  year: 2040, level: 'Mid',  fleet: 412000,  energy: 57.8,  depotShare: 76, available: false },
  { scenario: '2040 High', year: 2040, level: 'High', fleet: 2320000, energy: 245.9, depotShare: 83, available: false },
];

const AVAILABLE = NCI_DATA.filter((r) => r.available);
const COLORS_BY_YEAR: Record<number, string> = {
  2030: '#3b82f6',
  2035: '#0ea5e9',
  2040: '#cbd5e1',
};
const POWER_PALETTE = [
  '#94a3b8', '#cbd5e1', '#a78bfa', '#0ea5e9',
  '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#1e293b',
];
const stackedShare = AVAILABLE.map((r) => ({
  scenario: r.scenario,
  Depot: r.depotShare,
  Public: 100 - r.depotShare,
}));

// ---------------------------------------------------------------------------
// TEIS data (single CSV at datasets/teis/charging_events/charging_sessions.csv).
// ---------------------------------------------------------------------------

interface TeisJson {
  headline: {
    total_sessions: number;
    total_energy_gwh: number;
    avg_session_kwh: number;
    avg_session_min: number;
    pct_depot: number;
    pct_public: number;
    pct_enroute: number;
  };
  by_location: Array<{ loc_type: string; sessions: number; energy_mwh: number }>;
  by_market_segment: Array<{ market_segment: string; sessions: number; energy_mwh: number }>;
  by_power_level: Array<{ charging_pwr: number; sessions: number; energy_mwh: number }>;
  sessions_by_hour: number[];
  energy_by_hour_mwh: number[];
  soc_distribution: Array<{ bin: string; sessions: number }>;
}
const TEIS: TeisJson = teisAggregates as TeisJson;
const TEIS_HOURLY = Array.from({ length: 24 }, (_, h) => ({
  hour: h,
  Sessions: TEIS.sessions_by_hour[h] ?? 0,
  'Energy (MWh)': TEIS.energy_by_hour_mwh[h] ?? 0,
}));

// ---------------------------------------------------------------------------
// CEC data (per-scenario aggregation produced by aggregate_cec.py from the
// MHD charger-load XLSX bundle under datasets/cec/).
// ---------------------------------------------------------------------------

interface CecScenario {
  label: string;
  headline: {
    rows: number;
    total_energy_gwh: number;
    total_chargers: number;
    unique_tracts: number;
    unique_counties: number;
    avg_kwh_per_charger: number;
  };
  by_location: Array<{
    loc_type: string;
    loc_label: string;
    chargers: number;
    energy_mwh: number;
  }>;
  by_homebase: Array<{ homebase: string; chargers: number; energy_mwh: number }>;
  by_adoption: Array<{ adoption: string; chargers: number; energy_mwh: number }>;
  by_power_level: Array<{ power_kw: number; chargers: number; energy_mwh: number }>;
  top_counties: Array<{ NAME: string; chargers: number; energy_mwh: number; tracts: number }>;
}

const CEC: Record<string, CecScenario> = (
  cecAggregates as { scenarios: Record<string, CecScenario> }
).scenarios;

// ---------------------------------------------------------------------------
// Top-level component — picks a view based on (project, datasetType).
// ---------------------------------------------------------------------------

interface InsightsProps {
  project: Project;
  datasetType: DatasetType;
}

export const Insights: React.FC<InsightsProps> = ({ project, datasetType }) => {
  return (
    <div style={styles.scroll}>
      <div style={styles.container}>
        <InsightsHeader project={project} datasetType={datasetType} />
        {project === 'nci' && datasetType === 'charging_events' && <NCIChargingEventsView />}
        {project === 'nci' && datasetType === 'load_profiles' && <NCILoadProfilesView />}
        {project === 'nci' && datasetType === 'chargers' && <NCIChargersView />}
        {project === 'nci' && datasetType === 'simulation_results' && <NCISimulationView />}
        {project === 'teis' && datasetType === 'charging_sessions' && <TEISChargingSessionsView />}
        {project === 'cec' && CEC[datasetType] && (
          <CECScenarioView scenarioKey={datasetType} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header (adaptive)
// ---------------------------------------------------------------------------

const TITLES: Partial<Record<`${Project}/${DatasetType}`, { title: string; sub: string }>> = {
  'nci/charging_events': {
    title: 'NCI - Charging Events',
    sub: 'Class 4-8 BEV charging activity across 6 of 9 NCI scenarios. Fleet sizes and depot/public split.',
  },
  'nci/load_profiles': {
    title: 'NCI - Load Profiles',
    sub: 'Daily energy by scenario and average system charging power by hour-of-day, computed from load_result CSVs.',
  },
  'nci/chargers': {
    title: 'NCI - Charger Infrastructure',
    sub: 'Charger counts, vehicle-to-charger ratios, and the power-level mix across 6 scenarios.',
  },
  'nci/simulation_results': {
    title: 'NCI - Simulation Results',
    sub: 'Full pickle bundle. Not pre-aggregated; load via pandas.read_pickle() for ad-hoc analysis.',
  },
  'teis/charging_sessions': {
    title: 'TEIS - Charging Sessions',
    sub: '2023 EPA scenario; per-session activity for 314,246 events totaling 44.0 GWh.',
  },
  'cec/mhd_charger_low_cap_2027': {
    title: 'CEC - MHD Charger Load (Low Cap 2027)',
    sub: 'Census-tract MHD charger load under the low charger-utilization assumption for 2027. ~25.5k tract-power-loctype rows across California.',
  },
  'cec/mhd_charger_base_cap_2027': {
    title: 'CEC - MHD Charger Load (Base Cap 2027)',
    sub: 'Census-tract MHD charger load under the base / mid charger-utilization assumption for 2027. ~24.7k tract-power-loctype rows.',
  },
  'cec/mhd_charger_low_cap_2030': {
    title: 'CEC - MHD Charger Load (Low Cap 2030)',
    sub: 'Census-tract MHD charger load under the low charger-utilization assumption for 2030. ~43.3k tract-power-loctype rows across California.',
  },
  'cec/mhd_charger_base_cap_2030': {
    title: 'CEC - MHD Charger Load (Base Cap 2030)',
    sub: 'Census-tract MHD charger load under the base / mid charger-utilization assumption for 2030. ~40.9k tract-power-loctype rows.',
  },
};

const InsightsHeader: React.FC<InsightsProps> = ({ project, datasetType }) => {
  const t = TITLES[`${project}/${datasetType}` as `${Project}/${DatasetType}`];
  return (
    <header style={styles.header}>
      <h1 style={styles.title}>{t?.title ?? 'Insights'}</h1>
      <p style={styles.subtitle}>{t?.sub ?? ''}</p>
    </header>
  );
};

// ---------------------------------------------------------------------------
// NCI - Charging Events view (fleet + depot/public split + table)
// ---------------------------------------------------------------------------

const NCIChargingEventsView: React.FC = () => (
  <>
    <section style={styles.cardsRow}>
      <StatCard label="Scenarios available" value="6 / 9" sub="2030 + 2035 - Low / Mid / High" />
      <StatCard label="Fleet size range" value="12k - 704k" sub="Class 4-8 BEVs across available scenarios" />
      <StatCard label="Depot share" value="73 - 79%" sub="depot dominates in every scenario" />
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Fleet size by scenario</h2>
      <p style={styles.chartSub}>Class 4-8 BEVs (log scale; range spans 60&times;)</p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={NCI_DATA} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="scenario" tick={{ fontSize: 11 }} />
          <YAxis scale="log" domain={[10000, 5000000]} tickFormatter={fmtCount} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(v) => [fmtCount(Number(v)) + ' BEVs', 'Fleet']}
            contentStyle={tooltipStyle}
          />
          <Bar dataKey="fleet" radius={[4, 4, 0, 0]}>
            {NCI_DATA.map((r, i) => (
              <Cell
                key={i}
                fill={r.available ? COLORS_BY_YEAR[r.year] : '#cbd5e1'}
                fillOpacity={r.available ? 1 : 0.5}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p style={styles.legend}>
        <span style={{ ...styles.swatch, background: COLORS_BY_YEAR[2030] }} /> 2030
        <span style={{ ...styles.swatch, background: COLORS_BY_YEAR[2035], marginLeft: 12 }} /> 2035
        <span style={{ ...styles.swatch, background: '#cbd5e1', marginLeft: 12 }} /> 2040 (not bundled)
      </p>
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Depot vs public share</h2>
      <p style={styles.chartSub}>Depot (private) charging dominates across all bundled scenarios.</p>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={stackedShare} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="scenario" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
          <Tooltip formatter={(v) => v + '%'} contentStyle={tooltipStyle} />
          <Legend />
          <Bar dataKey="Depot" stackId="share" fill="#0f172a" />
          <Bar dataKey="Public" stackId="share" fill="#f97316" />
        </BarChart>
      </ResponsiveContainer>
    </section>

    <NCIHeadlineTable />

    <Footer />
  </>
);

// ---------------------------------------------------------------------------
// NCI - Load Profiles view (daily energy + hourly profile)
// ---------------------------------------------------------------------------

const NCILoadProfilesView: React.FC = () => (
  <>
    <section style={styles.cardsRow}>
      <StatCard label="Daily energy range" value="2.2 - 84.4 GWh" sub="across 6 bundled scenarios" />
      <StatCard label="Peak system load" value={`${Math.max(...AGG.flatMap((s) => s.hourly_load_mw)).toFixed(0)} MW`} sub="2035 High during evening peak" />
      <StatCard label="Trough" value={`${Math.min(...AGG.flatMap((s) => s.hourly_load_mw)).toFixed(0)} MW`} sub="early morning, 2030 Low" />
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Daily charging energy by scenario</h2>
      <p style={styles.chartSub}>GWh per day; simulated values (within 4% of NCI targets)</p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={NCI_DATA} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="scenario" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit=" GWh" />
          <Tooltip
            formatter={(v) => [Number(v).toFixed(1) + ' GWh/day', 'Energy']}
            contentStyle={tooltipStyle}
          />
          <Bar dataKey="energy" radius={[4, 4, 0, 0]}>
            {NCI_DATA.map((r, i) => (
              <Cell
                key={i}
                fill={r.available ? COLORS_BY_YEAR[r.year] : '#cbd5e1'}
                fillOpacity={r.available ? 1 : 0.5}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Hourly load profile</h2>
      <p style={styles.chartSub}>
        Average system charging power by hour-of-day (MW), aggregated across the 3-day simulation
        window. Unmanaged charging &mdash; trucks plug in on arrival, producing an evening peak as
        fleets return to base.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={HOURLY_LOAD_DATA} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="hour"
            tick={{ fontSize: 11 }}
            tickFormatter={(h) => `${h}:00`}
            interval={2}
          />
          <YAxis tick={{ fontSize: 11 }} unit=" MW" />
          <Tooltip
            formatter={(v) => [`${Number(v).toFixed(1)} MW`, '']}
            labelFormatter={(h) => `Hour ${h}:00`}
            contentStyle={tooltipStyle}
          />
          <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
          {AGG.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={scenarioLabel(s)}
              stroke={COLORS_BY_YEAR[s.year]}
              strokeWidth={s.level === 'High' ? 2.5 : 1.5}
              strokeDasharray={s.level === 'Low' ? '4 3' : s.level === 'Mid' ? '8 0' : undefined}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p style={styles.legend}>Solid = High adoption, dashed = Low. Color by year.</p>
    </section>

    <NCIHeadlineTable />

    <Footer />
  </>
);

// ---------------------------------------------------------------------------
// NCI - Chargers view (totals, V2C, power-level mix)
// ---------------------------------------------------------------------------

const NCIChargersView: React.FC = () => (
  <>
    <section style={styles.cardsRow}>
      <StatCard
        label="Chargers across 6 scenarios"
        value={fmtCount(AGG.reduce((a, s) => a + s.chargers.total, 0))}
        sub="88-92% located at depots"
      />
      <StatCard
        label="V2C ratio range"
        value={`${Math.min(...AGG.map((s) => s.vehicle_to_charger_ratio)).toFixed(2)} - ${Math.max(...AGG.map((s) => s.vehicle_to_charger_ratio)).toFixed(2)}`}
        sub="rises with fleet scale (concurrency)"
      />
      <StatCard label="Dominant power level" value="150 kW" sub="DC fast across all scenarios" />
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Total chargers by scenario</h2>
      <p style={styles.chartSub}>
        Computed from <code>chargers_*original.csv</code>; log scale.
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={AGG} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={(s: AggScenario) => scenarioLabel(s)} tick={{ fontSize: 11 }} />
          <YAxis scale="log" domain={[1000, 1_000_000]} tickFormatter={fmtCount} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(v) => [fmtCount(Number(v)), 'Chargers']}
            contentStyle={tooltipStyle}
          />
          <Bar dataKey="chargers.total" radius={[4, 4, 0, 0]}>
            {AGG.map((r, i) => (
              <Cell key={i} fill={COLORS_BY_YEAR[r.year]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Vehicle-to-charger ratio</h2>
      <p style={styles.chartSub}>
        Rises with fleet scale &mdash; reflecting greater concurrent use of shared chargers as
        adoption grows.
      </p>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={AGG} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={(s: AggScenario) => scenarioLabel(s)} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} />
          <Tooltip
            formatter={(v) => [Number(v).toFixed(2), 'Vehicles per charger']}
            contentStyle={tooltipStyle}
          />
          <Bar dataKey="vehicle_to_charger_ratio" radius={[4, 4, 0, 0]} fill="#0ea5e9">
            {AGG.map((r, i) => (
              <Cell key={i} fill={COLORS_BY_YEAR[r.year]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </section>

    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Charger mix by power level</h2>
      <p style={styles.chartSub}>
        Counts grouped by AC/DC power rating (kW), log scale. 150 kW DC fast is overwhelmingly
        dominant across all scenarios.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={POWER_MIX_DATA} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="scenario" tick={{ fontSize: 11 }} />
          <YAxis scale="log" domain={[1, 1_000_000]} tickFormatter={fmtCount} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => fmtCount(Number(v))} contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          {POWER_LEVELS.map((lvl, i) => (
            <Bar
              key={lvl}
              dataKey={`${lvl} kW`}
              fill={POWER_PALETTE[i % POWER_PALETTE.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </section>

    <Footer />
  </>
);

// ---------------------------------------------------------------------------
// NCI - Simulation Results view (informational only)
// ---------------------------------------------------------------------------

const NCISimulationView: React.FC = () => (
  <>
    <section style={styles.chartCard}>
      <h2 style={styles.chartTitle}>Full simulation pickles</h2>
      <p style={styles.chartSub}>
        One pickle per scenario at <code>datasets/nci/&lt;year&gt;/&lt;level&gt;/simulation_results_*.pickle</code>.
        These are the complete simulation state objects; load with{' '}
        <code>pandas.read_pickle()</code> for ad-hoc analysis. Not pre-aggregated.
      </p>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Scenario</th>
            <th style={styles.thRight}>Pickle size</th>
            <th style={styles.thRight}>Fleet</th>
          </tr>
        </thead>
        <tbody>
          {([
            ['2030 Low', '14 MB', 12000],
            ['2030 Mid', '35 MB', 24000],
            ['2030 High', '93 MB', 76000],
            ['2035 Low', '67 MB', 65000],
            ['2035 Mid', '162 MB', 134000],
            ['2035 High', '702 MB', 704000],
          ] as const).map(([name, size, fleet]) => (
            <tr key={name}>
              <td style={styles.td}>{name}</td>
              <td style={styles.tdRight}>{size}</td>
              <td style={styles.tdRight}>{fmtCount(fleet)} BEVs</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={styles.legend}>
        Plus depot-event pickles up to 1.25 GB (2035 High). Total bundle ~6.5 GB.
      </p>
    </section>

    <Footer />
  </>
);

// ---------------------------------------------------------------------------
// Reusable: NCI headline stats table
// ---------------------------------------------------------------------------

const NCIHeadlineTable: React.FC = () => (
  <section style={styles.chartCard}>
    <h2 style={styles.chartTitle}>Headline stats</h2>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Scenario</th>
          <th style={styles.thRight}>Fleet (BEV)</th>
          <th style={styles.thRight}>Daily Energy</th>
          <th style={styles.thRight}>Depot Share</th>
          <th style={styles.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {NCI_DATA.map((r) => (
          <tr key={r.scenario} style={r.available ? undefined : styles.rowMuted}>
            <td style={styles.td}>{r.scenario}</td>
            <td style={styles.tdRight}>{fmtCount(r.fleet)}</td>
            <td style={styles.tdRight}>{r.energy.toFixed(1)} GWh</td>
            <td style={styles.tdRight}>{r.depotShare}%</td>
            <td style={styles.td}>
              {r.available ? (
                <span style={styles.badgeOk}>bundled</span>
              ) : (
                <span style={styles.badgeMuted}>not bundled</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

// ---------------------------------------------------------------------------
// TEIS - Charging Sessions view
// ---------------------------------------------------------------------------

const LOC_LABELS: Record<string, string> = {
  dst_depot: 'Depot',
  dst_public: 'Public',
  enroute: 'Enroute',
};

const TEISChargingSessionsView: React.FC = () => {
  const locData = TEIS.by_location.map((r) => ({
    location: LOC_LABELS[r.loc_type] ?? r.loc_type,
    sessions: r.sessions,
    energy_mwh: r.energy_mwh,
  }));
  const segmentData = TEIS.by_market_segment.map((r) => ({
    segment: r.market_segment.replace(/_/g, ' '),
    sessions: r.sessions,
    energy_mwh: r.energy_mwh,
  }));
  const powerData = TEIS.by_power_level.map((r) => ({
    power: `${r.charging_pwr} kW`,
    sessions: r.sessions,
    energy_mwh: r.energy_mwh,
  }));

  return (
    <>
      <section style={styles.cardsRow}>
        <StatCard
          label="Sessions"
          value={fmtCount(TEIS.headline.total_sessions)}
          sub="3-day simulation, 2023 EPA scenario"
        />
        <StatCard
          label="Total energy"
          value={`${TEIS.headline.total_energy_gwh.toFixed(1)} GWh`}
          sub={`avg ${TEIS.headline.avg_session_kwh} kWh per session`}
        />
        <StatCard
          label="Avg duration"
          value={`${TEIS.headline.avg_session_min.toFixed(0)} min`}
          sub="across all session types"
        />
        <StatCard
          label="Location split"
          value={`${TEIS.headline.pct_depot.toFixed(0)}% / ${TEIS.headline.pct_public.toFixed(0)}% / ${TEIS.headline.pct_enroute.toFixed(0)}%`}
          sub="depot / public / enroute"
        />
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Sessions and energy by location</h2>
        <p style={styles.chartSub}>Depot dominates session count; enroute has higher energy-per-session.</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={locData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="location" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              unit=" MWh"
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar yAxisId="left" dataKey="sessions" fill="#3b82f6" name="Sessions" />
            <Bar yAxisId="right" dataKey="energy_mwh" fill="#0ea5e9" name="Energy (MWh)" />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Energy by market segment</h2>
        <p style={styles.chartSub}>
          Regional MDT and long-haul HDT lead total energy demand. 8 segments total.
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={segmentData}
            layout="vertical"
            margin={{ top: 8, right: 12, left: 80, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} unit=" MWh" />
            <YAxis type="category" dataKey="segment" tick={{ fontSize: 11 }} width={140} />
            <Tooltip
              formatter={(v) => [`${Number(v).toFixed(1)} MWh`, 'Energy']}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="energy_mwh" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Sessions by charger power level</h2>
        <p style={styles.chartSub}>
          150 kW DC fast leads session count; higher-power levels (350+ kW) deliver disproportionately more energy per session.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={powerData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="power" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              unit=" MWh"
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar yAxisId="left" dataKey="sessions" fill="#3b82f6" name="Sessions" />
            <Bar yAxisId="right" dataKey="energy_mwh" fill="#f59e0b" name="Energy (MWh)" />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Hourly session distribution</h2>
        <p style={styles.chartSub}>
          Session starts and energy demand by hour-of-day. Late-night peak (10 PM - midnight) plus
          steady afternoon activity.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={TEIS_HOURLY} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 11 }}
              tickFormatter={(h) => `${h}:00`}
              interval={2}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              unit=" MWh"
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Sessions"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Energy (MWh)"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>State-of-charge distribution at session start</h2>
        <p style={styles.chartSub}>
          Sessions binned by SOC at plug-in. The 90-100% bucket is large because many sessions are
          brief top-ups (en-route or destination short stops).
        </p>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={TEIS.soc_distribution} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="bin" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <Tooltip
              formatter={(v) => [fmtCount(Number(v)), 'Sessions']}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="sessions" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <Footer teis />
    </>
  );
};

// ---------------------------------------------------------------------------
// CEC - MHD Charger scenario view
// ---------------------------------------------------------------------------

const CEC_LOC_COLORS: Record<string, string> = {
  Depot: '#0f172a',
  Public: '#f97316',
  Enroute: '#0ea5e9',
};

const CECScenarioView: React.FC<{ scenarioKey: string }> = ({ scenarioKey }) => {
  const s = CEC[scenarioKey];
  if (!s) return null;

  const locData = s.by_location.map((r) => ({
    location: r.loc_label,
    chargers: r.chargers,
    energy_mwh: r.energy_mwh,
  }));
  const homebaseData = s.by_homebase.map((r) => ({
    homebase: r.homebase,
    chargers: r.chargers,
    energy_mwh: r.energy_mwh,
  }));
  const adoptionData = s.by_adoption.map((r) => ({
    adoption: r.adoption,
    chargers: r.chargers,
    energy_mwh: r.energy_mwh,
  }));
  const powerData = s.by_power_level.map((r) => ({
    power: `${r.power_kw} kW`,
    chargers: r.chargers,
    energy_mwh: r.energy_mwh,
  }));
  const countyData = s.top_counties.map((r) => ({
    county: r.NAME,
    chargers: r.chargers,
    energy_mwh: r.energy_mwh,
    tracts: r.tracts,
  }));

  return (
    <>
      <section style={styles.cardsRow}>
        <StatCard
          label="Total chargers"
          value={fmtCount(Math.round(s.headline.total_chargers))}
          sub={`${s.label} - California-wide MHD fleet`}
        />
        <StatCard
          label="Daily energy"
          value={`${s.headline.total_energy_gwh.toFixed(2)} GWh`}
          sub={`avg ${s.headline.avg_kwh_per_charger.toFixed(0)} kWh per charger`}
        />
        <StatCard
          label="Census tracts served"
          value={fmtCount(s.headline.unique_tracts)}
          sub={`across ${s.headline.unique_counties} CA counties`}
        />
        <StatCard
          label="Tract-power-loctype rows"
          value={fmtCount(s.headline.rows)}
          sub="raw bins in the source XLSX"
        />
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Chargers and energy by location type</h2>
        <p style={styles.chartSub}>
          Depot dominates both charger count and total daily energy; enroute delivers more energy
          per charger (higher utilization).
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={locData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="location" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit=" MWh" />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar yAxisId="left" dataKey="chargers" name="Chargers" radius={[4, 4, 0, 0]}>
              {locData.map((d, i) => (
                <Cell key={i} fill={CEC_LOC_COLORS[d.location] ?? '#3b82f6'} />
              ))}
            </Bar>
            <Bar yAxisId="right" dataKey="energy_mwh" name="Energy (MWh)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Chargers by power level</h2>
        <p style={styles.chartSub}>
          150 kW DC fast leads charger count under the low-cap scenario; the 350 kW tier delivers
          the most total energy thanks to higher per-charger throughput.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={powerData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="power" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit=" MWh" />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Bar yAxisId="left" dataKey="chargers" name="Chargers" fill="#3b82f6" />
            <Bar yAxisId="right" dataKey="energy_mwh" name="Energy (MWh)" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Homebase and adoption split</h2>
        <p style={styles.chartSub}>
          Where vehicles originate (origin / non-origin / enroute) and which adoption tier the
          chargers serve (base vs. non-base fleets).
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={homebaseData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="homebase" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
                <Tooltip
                  formatter={(v) => [fmtCount(Number(v)), 'Chargers']}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="chargers" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p style={styles.legend}>Chargers by homebase</p>
          </div>
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={adoptionData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="adoption" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
                <Tooltip
                  formatter={(v) => [fmtCount(Number(v)), 'Chargers']}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="chargers" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p style={styles.legend}>Chargers by adoption tier</p>
          </div>
        </div>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Top 10 counties by charger count</h2>
        <p style={styles.chartSub}>
          Los Angeles, San Bernardino, and Riverside lead by a wide margin; together the top three
          host roughly 45% of California's MHD chargers under this scenario.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={countyData}
            layout="vertical"
            margin={{ top: 8, right: 12, left: 90, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtCount} />
            <YAxis type="category" dataKey="county" tick={{ fontSize: 11 }} width={110} />
            <Tooltip
              formatter={(v) => [fmtCount(Number(v)), 'Chargers']}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="chargers" fill="#3b82f6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Top counties - detail</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>County</th>
              <th style={styles.thRight}>Chargers</th>
              <th style={styles.thRight}>Energy (MWh/day)</th>
              <th style={styles.thRight}>Tracts</th>
            </tr>
          </thead>
          <tbody>
            {countyData.map((r) => (
              <tr key={r.county}>
                <td style={styles.td}>{r.county}</td>
                <td style={styles.tdRight}>{fmtCount(Math.round(r.chargers))}</td>
                <td style={styles.tdRight}>{r.energy_mwh.toFixed(1)}</td>
                <td style={styles.tdRight}>{r.tracts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <Footer cec />
    </>
  );
};

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

const Footer: React.FC<{ teis?: boolean; cec?: boolean }> = ({ teis, cec }) => {
  if (cec) {
    return (
      <footer style={styles.footer}>
        All CEC stats are aggregated directly from the
        <code> charger_load_by_location_*.xlsx</code> files under
        <code> datasets/cec/</code> by <code>aggregate_cec.py</code> and read from
        <code> cec_aggregates.json</code>.
      </footer>
    );
  }
  if (teis) {
    return (
      <footer style={styles.footer}>
        All TEIS stats are aggregated directly from{' '}
        <code>datasets/teis/charging_events/charging_sessions.csv</code> by
        <code> aggregate_teis.py</code> and read from <code>teis_aggregates.json</code>.
      </footer>
    );
  }
  return (
    <footer style={styles.footer}>
      Fleet, daily energy, and depot share are the validated NCI headline values from the
      HEVI-LOAD simulation deck (April 2026). Charger counts, power-level mix,
      vehicle-to-charger ratio, and hourly load profile are aggregated directly from the
      bundled CSVs by <code>aggregate_nci.py</code> and read from{' '}
      <code>nci_aggregates.json</code>.
    </footer>
  );
};

// ---------------------------------------------------------------------------
// Helpers and styles
// ---------------------------------------------------------------------------

const StatCard: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
  <div style={styles.card}>
    <div style={styles.cardLabel}>{label}</div>
    <div style={styles.cardValue}>{value}</div>
    <div style={styles.cardSub}>{sub}</div>
  </div>
);

const fmtCount = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'k';
  return n.toString();
};

const tooltipStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  fontSize: '0.85rem',
};

const styles: Record<string, React.CSSProperties> = {
  scroll: {
    flex: 1,
    overflowY: 'auto',
    backgroundColor: '#f8fafc',
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '2rem 2rem 3rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  header: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  title: { fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '0.95rem', color: '#64748b', margin: 0, lineHeight: 1.5 },
  cardsRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  card: {
    flex: '1 1 220px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '1rem 1.2rem',
  },
  cardLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  cardValue: { fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: '0.4rem 0 0.3rem' },
  cardSub: { fontSize: '0.78rem', color: '#94a3b8' },
  chartCard: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '1.25rem',
  },
  chartTitle: { fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', margin: '0 0 0.25rem' },
  chartSub: { fontSize: '0.82rem', color: '#64748b', margin: '0 0 0.75rem' },
  legend: {
    fontSize: '0.78rem',
    color: '#475569',
    marginTop: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  swatch: { display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    textAlign: 'left',
    padding: '0.5rem 0.6rem',
    fontWeight: 600,
    color: '#475569',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  thRight: {
    textAlign: 'right',
    padding: '0.5rem 0.6rem',
    fontWeight: 600,
    color: '#475569',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  td: { padding: '0.45rem 0.6rem', color: '#0f172a', borderBottom: '1px solid #f1f5f9' },
  tdRight: {
    padding: '0.45rem 0.6rem',
    color: '#0f172a',
    borderBottom: '1px solid #f1f5f9',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  rowMuted: { opacity: 0.55 },
  badgeOk: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#065f46',
    backgroundColor: '#d1fae5',
    borderRadius: 999,
  },
  badgeMuted: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    borderRadius: 999,
  },
  footer: {
    fontSize: '0.78rem',
    color: '#94a3b8',
    textAlign: 'center',
    padding: '0.5rem 1rem',
    lineHeight: 1.6,
  },
};
